import React, { FC, useEffect, useRef } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import Picture from '../../../@shared/picture';
import RoutableNotEditableLink from '../../../@shared/routableNotEditableLink';
import Icon from '../../../@shared/icon';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import { TBrochureCardProps } from '../../types/downloadBrochures.types';
import { iconSize } from '../../../../constants/icon.constants';
import useDateValidate from '../../hooks/useDateValidate';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import '../../../../styles/@shared/downloadBrochures/brochureCard/styles.scss';
import useFormatDate from '../../../../hooks/useFormatDate';
import { googleClickCtaEvent } from '../../../../utils/analytics';

const BrochureCard: FC<WithTranslation & TBrochureCardProps> = (props) => {
  // eslint-disable-next-line @typescript-eslint/camelcase
  const { title, alt, size, full, icon, alternateImageUrl, thumbnail, digitalBrochureUrl, parsedBrochureDate, componentNumber } = props;

  const desktopRendition = alternateImageUrl ? { url: `${alternateImageUrl}&t=Size500` } : { url: `${thumbnail}&t=Size500` };
  const mobileRendition = alternateImageUrl ? { url: `${alternateImageUrl}&t=Size500` } : { url: `${thumbnail}&t=Size300` };
  const isNewCard = useDateValidate(parsedBrochureDate);
  const cardDate = useFormatDate(parsedBrochureDate);
  const { t } = props;

  const linkWrapperRef = useRef<HTMLSpanElement>(null);
  const a2aKitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (a2aKitRef.current && (e.target === a2aKitRef.current || a2aKitRef.current.contains(e.target as Node))) {
        googleClickCtaEvent('Brochure', t('cnhi-share'), title, componentNumber);
      }
    };
    linkWrapperRef?.current?.addEventListener('click', handleClick, true);

    return () => {
      linkWrapperRef?.current?.removeEventListener('click', handleClick, true);
    };
  }, [componentNumber, linkWrapperRef, a2aKitRef]);

  return (
    <article className="download-brochure_card__article">
      <div className="download-brochure_card__image-container">
        {isNewCard ? (
          <div className="download-brochure_card__badge">
            <DictionaryTerm keyword="cnhi-download-brochure-listing-badge" customTextTag="p" />
          </div>
        ) : null}
        <Picture
          productImageProps={{
            // eslint-disable-next-line @typescript-eslint/camelcase
            mobileRendition: mobileRendition,
            // eslint-disable-next-line @typescript-eslint/camelcase
            desktopRendition: desktopRendition,
            pictureClassName: 'special-offer-brochure-card__article__picture--fixed-aspect-ratio',
            alt: alt,
          }}
        />
      </div>

      <div className="download-brochure_card__content-block">
        {title ? <h2 className="download-brochure_card__title">{title}</h2> : null}
        {size ? <span className="download-brochure_card__sub-heading"> {size} MB </span> : null}
        {cardDate && size && <p className="download-brochure_card__separator">&nbsp;|&nbsp;</p>}
        {cardDate && <Text field={{ value: cardDate }} tag="p" className={'download-brochure_card__date'} />}
      </div>
      <div className="download-brochure_card__link-wrapper">
        {digitalBrochureUrl ? (
          <RoutableNotEditableLink
            href={digitalBrochureUrl}
            target="_blank"
            className={'download-brochure_card__link download-brochure_card__link--view'}
          >
            <span className="download-brochure_card__link download-brochure_card__link--label">
              <DictionaryTerm keyword="cnhi-view" />
            </span>
            <Icon iconField={icon?.[2]} color="primary" extraClassName="download-brochure_card__link__icon" size={iconSize.mbl} />
          </RoutableNotEditableLink>
        ) : (
          <div className="a2a_kit a2a_kit_size_32 a2a_default_style" ref={a2aKitRef}>
            <a
              className="a2a_dd download-brochure_card__link download-brochure_card__link--share download-brochure_card__add-to-any"
              data-a2a-url={full}
              data-a2a-title={alt}
            >
              <span role="button" className="download-brochure_card__link--label" ref={linkWrapperRef}>
                <DictionaryTerm keyword="cnhi-share" />
              </span>
              <Icon iconField={icon?.[0]} color={'primary'} size={iconSize['mbl']} />
            </a>
          </div>
        )}
        <RoutableNotEditableLink
          href={full}
          target="_blank"
          className={'download-brochure_card__link download-brochure_card__link--download'}
          onClick={() => googleClickCtaEvent('Brochure', t('cnhi-download'), title, componentNumber)}
        >
          <span className="download-brochure_card__link download-brochure_card__link--label">
            <DictionaryTerm keyword="cnhi-download" />
          </span>
          <Icon iconField={icon?.[1]} color={'primary'} extraClassName="download-brochure_card__link__icon" size={iconSize['mbl']} />
        </RoutableNotEditableLink>
      </div>
    </article>
  );
};

export default withTranslation()(BrochureCard);
